import { useEffect, useMemo, useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import toast from "react-hot-toast";
import {
  getFacturasParaVincularCosto,
  getVinculadoNetoPorCosto,
  totalConIvaCosto,
  vincularDocumentoCosto,
} from "../../utils/costosProyecto";
import { useCurrency } from "../../utils/useCurrency";

// Sub-modal "Vincular factura" (menú ⋮ de CarteleriaCostosModal, Fase 4 — 2026-10-04).
//
// Vincula (parte de) una factura YA CLASIFICADA en Compras a esta línea de costo. NO crea ni edita
// facturas/activos/pagos — solo escribe el subledger `costos_proyecto_vinculos` vía la RPC
// `vincular_documento_costo` (10.190). La RPC vuelve a validar TODO del lado de la base (proveedor,
// país, clasificación, coherencia Gasto/Activo vs modalidad, topes); este modal solo filtra la lista
// para que el usuario no tenga que adivinar cuáles facturas son candidatas.
//
// Fix 2026-10-04: `getFacturasParaVincularCosto` ya esconde las facturas totalmente cubiertas en
// CUALQUIER otra línea/proyecto y agrega `disponibleNeto` a las parcialmente vinculadas (fuente
// única `calcularDisponibleVinculo`, `src/utils/costosProyecto.js`) — el default propuesto al elegir
// una factura es el MENOR entre ese disponible y el remanente de la línea actual.
const VincularFacturaCostoModal = ({ show, costo, negocio, userId, onHide, onVinculado }) => {
  const [cargando, setCargando] = useState(true);
  const [facturas, setFacturas] = useState([]);
  const [remanenteLinea, setRemanenteLinea] = useState(null);
  const [seleccion, setSeleccion] = useState(""); // `${documentoTipo}:${documentoOrigenId}`
  const [montoNeto, setMontoNeto] = useState("");
  const [guardando, setGuardando] = useState(false);

  const { formatAmount } = useCurrency();
  const pais = negocio.pais;

  useEffect(() => {
    let vivo = true;
    (async () => {
      try {
        const [candidatas, vinculadoMap] = await Promise.all([
          getFacturasParaVincularCosto({ pais, proveedorId: costo.proveedor_id, modalidad: negocio.modalidad }),
          getVinculadoNetoPorCosto([costo.id]),
        ]);
        if (!vivo) return;
        setFacturas(candidatas);
        setRemanenteLinea(Number(costo.monto) - (vinculadoMap[costo.id] || 0));
      } catch (e) {
        toast.error(`Error al cargar facturas candidatas: ${e.message || e}`);
      } finally {
        if (vivo) setCargando(false);
      }
    })();
    return () => {
      vivo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [costo.id]);

  const facturaSeleccionada = useMemo(
    () => facturas.find((f) => `${f.documentoTipo}:${f.documentoOrigenId}` === seleccion) || null,
    [facturas, seleccion]
  );

  const handleSeleccionar = (value) => {
    setSeleccion(value);
    const f = facturas.find((x) => `${x.documentoTipo}:${x.documentoOrigenId}` === value);
    if (f && remanenteLinea != null) {
      // Default: lo menor entre lo que queda por cubrir de la línea y lo que queda DISPONIBLE de la
      // factura (no su neto total — puede estar parcialmente vinculada en otra línea/proyecto, fix
      // 2026-10-04). Misma moneda asumida — si la factura está en otra moneda, el usuario ajusta a
      // mano, sin FX automático.
      setMontoNeto(String(Math.max(0, Math.min(remanenteLinea, f.disponibleNeto))));
    }
  };

  const handleGuardar = async () => {
    if (!facturaSeleccionada) return toast.error("Seleccioná una factura");
    const neto = Number(montoNeto);
    if (!neto || neto <= 0) return toast.error("El monto a vincular debe ser mayor a 0");
    setGuardando(true);
    try {
      const { total: bruto } = totalConIvaCosto(neto, costo.moneda, pais, costo.exento_iva);
      const resultado = await vincularDocumentoCosto({
        costoId: costo.id,
        documentoTipo: facturaSeleccionada.documentoTipo,
        documentoOrigenId: facturaSeleccionada.documentoOrigenId,
        montoNeto: neto,
        montoBruto: bruto,
        usuarioId: Number.isFinite(Number(userId)) ? Number(userId) : null,
      });
      toast.success("Factura vinculada");
      onVinculado?.(resultado);
    } catch (err) {
      // 10.177: el mensaje real de la RPC (candados de proveedor/país/clasificación/topes), tal cual.
      toast.error("Error al vincular: " + (err.message || err));
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>Vincular factura — {costo?.concepto}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {cargando ? (
          <div style={{ textAlign: "center", padding: 20 }}>Cargando…</div>
        ) : (
          <>
            <div style={{ fontSize: "0.8rem", color: "#6c757d", marginBottom: 12 }}>
              Remanente de la línea: <strong>{formatAmount(Math.max(0, remanenteLinea || 0), costo.moneda)}</strong>{" "}
              (neto). Solo se listan facturas de este proveedor, ya clasificadas en Compras, del tipo que corresponde a
              esta modalidad ({negocio.modalidad === "OPEX" ? "Activo" : "Gasto"}).
            </div>

            {facturas.length === 0 ? (
              <div style={{ color: "#999", textAlign: "center", padding: 20 }}>
                No hay facturas candidatas de este proveedor
                {negocio.modalidad === "OPEX" ? " clasificadas como Activo" : " clasificadas como Gasto"}.
              </div>
            ) : (
              <Form.Group className="mb-3">
                <Form.Label>Factura</Form.Label>
                <Form.Select value={seleccion} onChange={(e) => handleSeleccionar(e.target.value)}>
                  <option value="">Seleccioná una factura…</option>
                  {facturas.map((f) => (
                    <option
                      key={`${f.documentoTipo}:${f.documentoOrigenId}`}
                      value={`${f.documentoTipo}:${f.documentoOrigenId}`}
                    >
                      {f.label} — {f.fechaEmision || "s/fecha"} —{" "}
                      {f.disponibleNeto < f.montoNeto
                        ? `disponible ${formatAmount(f.disponibleNeto, "CLP")} de ${formatAmount(f.montoNeto, "CLP")}`
                        : `neto ${formatAmount(f.montoNeto, "CLP")}`}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            )}

            {facturaSeleccionada && (
              <Form.Group className="mb-3">
                <Form.Label>Monto a vincular (neto, en {costo.moneda})</Form.Label>
                <Form.Control type="number" min="0" value={montoNeto} onChange={(e) => setMontoNeto(e.target.value)} />
                <Form.Text className="text-muted">
                  Si la factura está en una moneda distinta a la de esta línea, ingresá el equivalente en {costo.moneda}{" "}
                  — sin conversión automática.
                </Form.Text>
              </Form.Group>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={guardando}>
          Cancelar
        </Button>
        <Button variant="primary" onClick={handleGuardar} disabled={guardando || !facturaSeleccionada}>
          {guardando ? "Vinculando…" : "Vincular"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default VincularFacturaCostoModal;
